/**
 * ConversionProgress Component
 *
 * Displays conversion progress with animated progress bar.
 */

import { memo } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

interface ConversionProgressProps {
  fileName?: string;
  progress: number;
  statusMessage: string;
  outputFormat: string;
}

export const ConversionProgress = memo(function ConversionProgress({
  fileName,
  progress,
  statusMessage,
  outputFormat,
}: ConversionProgressProps) {
  const { t } = useTranslation();

  const outputFileName = fileName?.replace(/\.[^.]+$/, outputFormat) || 'output' + outputFormat;

  return (
    <div className="card-themed-lg">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
          <RefreshCw className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" />
        </div>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
          {t('converter.progress.converting', {
            defaultValue: 'Converting...',
          })}
        </h3>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
          {fileName} &rarr; {outputFileName}
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-secondary-600 dark:text-secondary-400 mb-2">
          <span>{statusMessage}</span>
          <span className="font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="w-full bg-secondary-200 dark:bg-secondary-600 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-primary-500 to-primary-600 h-full transition-all duration-500 ease-out rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Estimated time */}
      {progress > 0 && progress < 100 && (
        <p className="text-center text-sm text-secondary-500 dark:text-secondary-400">
          {t('converter.progress.pleaseWait', { defaultValue: 'Please wait...' })}
        </p>
      )}
    </div>
  );
});

export default ConversionProgress;
